<template>
  <div :class="{ 'customer-reservation': true, 'customer-reservation-odd': itemStyle === 'odd' }">
    <avatar
      v-if="!isReservationForDays && reservationData.worker"
      :worker="reservationData.worker"
      :name="reservationData.worker.name" />

    <div class="basic-info">
      <strong
        v-if="!isReservationForDays"
        class="date">
        {{ date }}
      </strong>

      <span
        v-if="isReservationForDays"
        class="date">
        {{ $t('day.from') }} <strong>{{ startDate }}</strong>
      </span>

      <span
        v-if="isReservationForDays"
        class="date">
        {{ $t('day.to') }} <strong>{{ endDate }}</strong>
      </span>

      <div v-if="!isReservationForDays">
        <span
          v-if="peopleNumberEnabled"
          class="people-number">
          <icon
            name="profile-big"
            primaryColor="color-text-light"
            size="xs" />
          <span class="people-number-counter">
            {{ peopleNumber }}
          </span>
        </span>

        <span class="time">
          {{ $t('customerReservation.hour') }}
          <span class="start-time">
            {{ startTime[0] }}<sup>{{ startTime[1] }}</sup>
          </span> -
          <span class="end-time">
            {{ endTime[0] }}<sup>{{ endTime[1] }}</sup>
          </span>
        </span>

        <periodicity-viewer
          :itemData="reservationData"
          infoBoxPosition="top"
          :parentSelectorWithOverflow="null"
          :rowMode="false"
          :contentMode="'simple'"
          :autoRenew="reservationData.periodicity_auto_renew" />

        <span class="duration">
          (
            <icon
              name="time"
              primaryColor="color-text-light"
              size="xxs" />
            {{ duration }}
          )
        </span>
      </div>

      <div class="status-wrapper">
        <span
          v-if="isReservationForDays && peopleNumberEnabled"
          class="people-number">
          <icon
            name="profile-big"
            primaryColor="color-text-light"
            size="xs" />
          <span class="people-number-counter">
            {{ peopleNumber }}<template v-if="childrenNumber">+{{ childrenNumber }}</template>
          </span>
        </span>

        {{ $t('customerReservation.status') }} <span :class="reservationStatusClass">{{ reservationStatus }}</span>
      </div>
    </div>

    <div class="additional-info">
      <span
        v-if="serviceName"
        class="service">
        {{ serviceName }}
      </span>

      <span
        v-if="hasPrice"
        class="price">
        <template v-if="userCanSeeFullPaymentsData">{{ $t('customerReservation.price') }} {{ totalPrice }} -</template>
        <span :class="paymentStatusClass">{{ paymentStatus }}</span>
      </span>

      <span class="present">
        {{ $t('customerReservation.presence') }}
        <template v-if="reservationData.present === 1">{{ $t('customerReservation.yes') }}</template>
        <template v-if="reservationData.present === 0">{{ $t('customerReservation.no') }}</template>
      </span>
    </div>

    <btn
      type="icon"
      :title="$t('common.ui.seeReservationDetails')"
      iconName="info-circle"
      iconColor="color-text-light"
      @click.native="showReservationDetails" />
  </div>
</template>

<script>
import Avatar from './../common/Avatar';
import Configuration from './../mixins/Configuration';
import PeriodicityViewer from '../common/PeriodicityViewer.vue';
import UserTypes from './../mixins/UserTypes';

export default {
  name: 'customer-reservation',
  props: ['reservationData', 'itemStyle'],
  mixins: [
    Configuration,
    UserTypes
  ],
  components: {
    'avatar': Avatar,
    'periodicity-viewer': PeriodicityViewer
  },
  computed: {
    isReservationForDays () {
      return this.reservationData.type === 1;
    },
    date () {
      let dateFormat = 'dddd, D MMMM YYYY';
      let reservationDate = this.reservationData.date;
      reservationDate = reservationDate.split('-');
      reservationDate[1] = parseInt(reservationDate[1], 10) - 1;
      let newTimestamp = new Date(reservationDate[0], reservationDate[1], reservationDate[2]);

      return this.$moment(newTimestamp).format(dateFormat);
    },
    startDate () {
      let dateFormat = 'dddd, D MMMM YYYY';
      let reservationDate = this.reservationData.start_at.split(' ')[0];
      reservationDate = reservationDate.split('-');
      reservationDate[1] = parseInt(reservationDate[1], 10) - 1;
      let newTimestamp = new Date(reservationDate[0], reservationDate[1], reservationDate[2]);

      return this.$moment(newTimestamp).format(dateFormat);
    },
    endDate () {
      let dateFormat = 'dddd, D MMMM YYYY';
      let reservationDate = this.reservationData.end_at.split(' ')[0];
      reservationDate = reservationDate.split('-');
      reservationDate[1] = parseInt(reservationDate[1], 10) - 1;
      let newTimestamp = new Date(reservationDate[0], reservationDate[1], reservationDate[2]);

      return this.$moment(newTimestamp).format(dateFormat);
    },
    startTime () {
      let startTime = this.reservationData.start_at.split(' ')[1];
      startTime = startTime.split(':');
      return [startTime[0], startTime[1]];
    },
    endTime () {
      let endTime = this.reservationData.end_at.split(' ')[1];
      endTime = endTime.split(':');
      return [endTime[0], endTime[1]];
    },
    duration () {
      return this.reservationData.duration + 'm';
    },
    childrenNumber () {
      return this.reservationData.children_number;
    },
    peopleNumber () {
      return this.reservationData.people_number;
    },
    serviceName () {
      if (this.reservationData.service !== null) {
        return this.reservationData.service.name;
      }

      return false;
    },
    hasPrice () {
      return this.reservationData.payment !== null;
    },
    totalPrice () {
      if (this.reservationData.payment !== null) {
        let priceParts = this.reservationData.payment.price.split('.');

        if (priceParts[1] === '00') {
          if (this.reservationData.payment && this.reservationData.payment.currency) {
            return priceParts[0] + ' ' + this.reservationData.payment.currency;
          }

          return priceParts[0] + ' ' + this.reservationData.configuration_payment.currency;
        }

        if (this.reservationData.payment && this.reservationData.payment.currency) {
          return this.reservationData.payment.price + ' ' + this.reservationData.payment.currency;
        }

        return this.reservationData.payment.price + ' ' + this.reservationData.payment.currency;
      }

      return false;
    },
    paymentStatus () {
      if (this.reservationData.payment.status === 'COMPLETED') {
        return this.$t('customerReservation.paid');
      }

      if (this.reservationData.payment.status === 'CASH') {
        return this.$t('common.ui.paidWithCash');
      }

      if (this.reservationData.payment.status === 'CARD_REGISTERED') {
        return this.$t('customerReservation.cardRegistered');
      }

      return this.$t('customerReservation.unpaid');
    },
    paymentStatusClass () {
      if (['CASH', 'COMPLETED', 'CARD_REGISTERED'].indexOf(this.reservationData.payment.status) > -1) {
        return 'is-paid';
      }

      return 'is-not-paid';
    },
    reservationStatus () {
      if (this.reservationData.status === 1) {
        return this.$t('customerReservation.waitsForAccept');
      }

      if (this.reservationData.status === 3) {
        if (
          this.reservationData.payment &&
          this.reservationData.payment.status === 'CARD_REGISTERED'
        ) {
          return this.$t('customerReservation.cardRegistered');
        }

        if (
          this.reservationData.payment &&
          !(this.reservationData.payment.status === 'COMPLETED' || this.reservationData.payment.status === 'CASH')
        ) {
          return this.$t('customerReservation.waitsForPayment');
        }

        return this.$t('customerReservation.accepted');
      }

      if (this.reservationData.status === 4) {
        return this.$t('customerReservation.rejected');
      }

      if (this.reservationData.status === 5) {
        return this.$t('customerReservation.cancelled');
      }

      if (this.reservationData.status === 6) {
        return this.$t('customerReservation.waitingList');
      }

      return '-';
    },
    reservationStatusClass () {
      if (this.reservationData.status === 1) {
        return 'status status-is-waiting-for-approve';
      }

      if (this.reservationData.status === 3) {
        if (
          this.reservationData.payment &&
          !(this.reservationData.payment.status === 'COMPLETED' || this.reservationData.payment.status === 'CASH')
        ) {
          return 'status status-is-waiting-for-payment';
        }

        return 'status status-is-accepted';
      }

      if (this.reservationData.status === 4) {
        return 'status status-is-rejected';
      }

      if (this.reservationData.status === 5) {
        return 'status status-is-cancelled';
      }

      if (this.reservationData.status === 6) {
        return 'status status-is-waiting-list';
      }

      return 'status status-is-unknown';
    }
  },
  methods: {
    showReservationDetails () {
      this.$bus.$emit('show-reservation-details-popup', this.reservationData.id);
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.customer-reservation {
  align-items: center;
  border-radius: $border-radius-small;
  display: flex;
  padding: 1rem 5rem 1rem 1.5rem;
  position: relative;

  &-odd {
    background: rgba(255, 255, 255, .05);
  }

  .basic-info {
    padding: 0 1rem;
    width: 50%;
  }

  .additional-info {
    display: flex;
    flex-wrap: wrap;
    font-size: 1.3rem;
    padding: 0 1rem 0 0;
    width: calc(100% - 50%);

    .service,
    .price,
    .present {
      display: block;
      width: 100%;
    }

    .service {
      color: $color-text-lightest;
    }

    .price,
    .present {
      margin-top: .5rem;
    }
  }

  .date {
    color: $color-light;
    display: block;
    font-size: 1.3rem;
    margin-bottom: .5rem;
  }

  .time {
    color: $color-light;
    font-size: 1.4rem;
    margin-left: .5rem;
  }

  .status-wrapper {
    font-size: 1.1rem;
    margin-top: .5rem;
    position: relative;
    top: .3rem;

    .status {
      &.status-is-waiting-for-approve,
      &.status-is-waiting-for-payment {
        color: $color-notice;
      }
      &.status-is-accepted {
        color: $color-success;
      }
      &.status-is-rejected,
      &.status-is-cancelled {
        color: $color-danger;
      }
      &.status-is-waiting-list {
        color: $color-warning;
      }
      &.status-is-unknown {
        color: $color-dark;
      }
    }
  }

  .duration {
    font-size: 1.4rem;
    margin-left: .5rem;

    .icon {
      position: relative;
      top: .1rem;
    }
  }

  .people-number {
    position: relative;
    top: .3rem;

    &-counter {
      background: $color-text-light;
      border: 2px solid $color-dark;
      border-radius: 1rem;
      color: $color-light;
      font-size: 1.2rem;
      font-weight: bold;
      margin-left: -.7rem;
      padding: .125rem .5rem;
      position: relative;
      top: -.4rem;
    }
  }

  .button {
    position: absolute;
    right: 1.5rem;
    top: 50%;
    transform: translateY(-50%);
  }

  .is-paid {
    color: $color-success;
  }

  .is-not-paid {
    color: $color-danger;
  }
}

@media (max-width: 600px) {
  .customer-reservation {
    .basic-info {
      width: 100%;
    }

    .duration {
      white-space: nowrap;
    }

    .additional-info {
      display: none;
    }
  }
}

@media (max-width: 400px) {
  .customer-reservation {
    padding: 1rem 9rem 1rem .5rem;

    .button {
      right: 5rem;

      & + .button {
        right: .5rem;
      }
    }

    .time,
    .duration {
      font-size: 1.3rem;
    }
  }

  .customer-reservation .date {
    font-size: 1.4rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ui/CustomerReservation.vue